import React from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export function NewsletterSignup() {
  const [email, setEmail] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("sending"); setError(null);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(), source: "newsletter_page", honeypot,
      });
      setStatus("sent");
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || "Couldn't sign you up — please try again.");
    }
  };

  return (
    <div className="max-w-4xl" data-testid="public-newsletter">
      <div className="overline-warm mb-4">Newsletter</div>
      <h1 className="display-lg mb-6">From the Workshop.</h1>

      <figure className="newsletter-hero-illustration mb-10">
        <img
          src="/images/workshop-newsletter-keyed.png"
          alt="Two writers at work in a cluttered workshop — walls pinned with notes and theory threads, a world map marked with red circles, the older of the two saying 'You're alive, then.'"
          className="newsletter-hero-image"
        />
        <figcaption className="caption-warm mt-3">
          The workshop at work. <span className="muted">And yes, she's alive.</span>
        </figcaption>
      </figure>

      <p className="lede max-w-2xl mb-6">
        A weekly note — publishing news, what I'm working on, occasional
        excerpts, the odd essay on AI or markets when it earns its place.
      </p>
      <p className="prose-warm max-w-2xl mb-10">
        Sent when there's something to say, never otherwise. Filtered of all
        the usual newsletter nonsense. Unsubscribe in one click.
      </p>

      {status === "sent" ? (
        <div className="contact-success" data-testid="newsletter-sent">
          <div className="overline-warm mb-2">Check your inbox</div>
          <p className="prose-warm">
            A confirmation email is on its way. Click the link inside to finish
            signing up. If it doesn't arrive within a few minutes, check your
            junk folder — and if it's still not there, try again.
          </p>
          <Link to="/" className="btn-warm-ghost mt-6 inline-block"
                data-testid="newsletter-home">
            Back to the home page
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5 max-w-md"
              data-testid="newsletter-form">
          <div>
            <label className="overline-warm mb-2 block" htmlFor="nl-email">Email</label>
            <input id="nl-email" type="email" value={email}
                   onChange={(e)=>setEmail(e.target.value)}
                   required data-testid="newsletter-email"
                   className="input-warm"/>
          </div>
          <input type="text" name="phone" tabIndex={-1} autoComplete="off"
                 value={honeypot} onChange={(e)=>setHoneypot(e.target.value)}
                 style={{position:"absolute", left:"-9999px", width:"1px", height:"1px"}}
                 aria-hidden="true"/>
          {error && <p className="contact-error" data-testid="newsletter-error">{error}</p>}
          <button type="submit" disabled={status === "sending"}
                  className="btn-warm-primary"
                  data-testid="newsletter-submit">
            {status === "sending" ? "Sending…" : "Subscribe"}
          </button>
          <p className="text-xs muted" style={{fontStyle:"italic"}}>
            You'll need to click a confirmation link before anything is sent.
          </p>
        </form>
      )}
    </div>
  );
}

export function NewsletterConfirm() {
  const { token } = useParams();
  const [state, setState] = React.useState("checking");
  const [msg, setMsg] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/newsletter/confirm/${token}`)
      .then(r => {
        if (r.data.ok) setState("confirmed");
        else { setState("error"); setMsg(r.data.error); }
      })
      .catch(() => { setState("error"); setMsg("Confirmation link invalid or expired."); });
  }, [token]);

  return (
    <div className="max-w-xl" data-testid="public-newsletter-confirm">
      <div className="overline-warm mb-4">Newsletter</div>
      {state === "checking" && <p className="muted">Confirming…</p>}
      {state === "confirmed" && (
        <>
          <h1 className="display-lg mb-6">You're in.</h1>
          <p className="prose-warm mb-8">
            Thank you. The next issue will reach you when there's something
            worth saying. Nothing before then.
          </p>
          <Link to="/" className="btn-warm-primary inline-block"
                data-testid="confirm-home">Back to the home page</Link>
        </>
      )}
      {state === "error" && (
        <>
          <h1 className="display-lg mb-6">Hm.</h1>
          <p className="prose-warm mb-6">
            {msg || "That confirmation link doesn't seem to work anymore."}
            &nbsp;You can try signing up again.
          </p>
          <Link to="/newsletter" className="btn-warm-primary inline-block">Sign up again</Link>
        </>
      )}
    </div>
  );
}

export function NewsletterUnsubscribe() {
  const { token } = useParams();
  const [state, setState] = React.useState("checking");
  const [msg, setMsg] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/newsletter/unsubscribe/${token}`)
      .then(r => {
        if (r.data.ok) setState("done");
        else { setState("error"); setMsg(r.data.error); }
      })
      .catch(() => { setState("error"); setMsg("Unsubscribe link invalid."); });
  }, [token]);

  return (
    <div className="max-w-xl" data-testid="public-newsletter-unsubscribe">
      <div className="overline-warm mb-4">Newsletter</div>
      {state === "checking" && <p className="muted">Processing…</p>}
      {state === "done" && (
        <>
          <h1 className="display-lg mb-6">Unsubscribed.</h1>
          <p className="prose-warm mb-8">
            Done — you'll receive no more issues. Thank you for reading while
            you did.
          </p>
          <Link to="/" className="btn-warm-primary inline-block">Home</Link>
        </>
      )}
      {state === "error" && (
        <>
          <h1 className="display-lg mb-6">Already done.</h1>
          <p className="prose-warm mb-6">
            {msg || "This unsubscribe link is no longer valid — you were already removed."}
          </p>
          <Link to="/" className="btn-warm-primary inline-block">Home</Link>
        </>
      )}
    </div>
  );
}
